import { type RefObject, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

import dayjs from 'dayjs';

import { useUpdateRecords } from '../features/records';
import Help from '../images/help.svg?react';
import { temperatureContext } from '../lib/context';
import { convertTemperature, convertToCelsius, convertToSecond, secondToTime } from '../lib/convert';
import './Configure.css';
import WorkingTimeRangesEditor from './WorkingTimeRangesEditor';

const LightTooltip = styled(({ className, ...props }: any) => <Tooltip {...props} classes={{ popper: className }} />)(
    ({ theme }: any) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: theme.palette.common.white,
            color: 'rgba(0, 0, 0, 0.87)',
            boxShadow: theme.shadows[3],
            fontSize: 10,
            padding: '10px 5px',
        },
    })
);

function Config(props: any) {
    const { deviceId, index, metaData, threshold, interval, notifyEmails, tKey, ro } = props;
    const { t } = useTranslation(undefined, { keyPrefix: tKey });
    const { t: tNotify } = useTranslation(undefined, { keyPrefix: 'Notification' });
    const updateRecords = useUpdateRecords();

    const tempUnit = useContext(temperatureContext);

    const thresholdConvert = secondToTime(threshold.time_limit);
    const intervalConvert = secondToTime(interval);

    const [saveAlert, setSaveAlert] = useState<'success' | 'error' | null>(null);

    const [rangeType, setRangeType] = useState(threshold.type);
    const [upperUnit, setUpperUnit] = useState(tempUnit);
    const [lowerUnit, setLowerUnit] = useState(tempUnit);
    const [timeUnit, setTimeUnit] = useState(thresholdConvert.unit);
    const [intervalUnit, setIntervalUnit] = useState(intervalConvert.unit);

    const now = dayjs();
    const timeOffset = now.utcOffset();
    const timeZoneOffset = new Date().getTimezoneOffset();
    const offsetHour = timeZoneOffset / 60;
    const offsetSign = offsetHour > 0 ? '-' : '+';
    const offsetString = `GMT${offsetSign}${Math.abs(offsetHour).toString()}`;

    const buildInitialRanges = (th: any) => {
        // API stores times in UTC, add timeOffset to convert to local time for display
        const toLocal = (time: any) => (time ? dayjs(time).add(timeOffset, 'minute') : null);

        if (th.alert_time && th.alert_time.length > 0) {
            return th.alert_time.map((r: any) => ({
                start: toLocal(r.start_time),
                end: toLocal(r.end_time),
            }));
        }
        // Fallback to single start/end time for backwards compatibility
        if (th.alert_start_time || th.alert_end_time) {
            return [{ start: toLocal(th.alert_start_time), end: toLocal(th.alert_end_time) }];
        }
        return [{ start: null, end: null }];
    };

    const [alertRanges, setAlertRanges] = useState(() => buildInitialRanges(threshold));
    const shipmentNumberInput = useRef<HTMLInputElement>(null);
    const shipperInput = useRef<HTMLInputElement>(null);
    const carrierInput = useRef<HTMLInputElement>(null);
    const consigneeInput = useRef<HTMLInputElement>(null);
    const startLocationInput = useRef<HTMLInputElement>(null);
    const endLocationInput = useRef<HTMLInputElement>(null);
    const noteInput = useRef<HTMLInputElement>(null);
    const upLimitInput = useRef<HTMLInputElement>(null);
    const lowLimitInput = useRef<HTMLInputElement>(null);
    const timeLimitInput = useRef<HTMLInputElement>(null);
    const intervalInput = useRef<HTMLInputElement>(null);
    const emailInput1 = useRef<HTMLInputElement>(null);
    const emailInput2 = useRef<HTMLInputElement>(null);
    const emailInput3 = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setUpperUnit(tempUnit);
        setLowerUnit(tempUnit);
    }, [tempUnit]);

    useEffect(() => {
        setAlertRanges(buildInitialRanges(threshold));
    }, [threshold]);

    const handleWorkingTimeChange = useCallback((ranges: any) => {
        setAlertRanges(ranges);
    }, []);

    const handleCloseSaveAlert = (event?: any, reason?: any) => {
        if (reason === 'clickaway') {
            return;
        }
        setSaveAlert(null);
    };

    // Fallback satisfies RefObject's typed null; every ref here is attached to a rendered input.
    const valueOf = (ref: RefObject<HTMLInputElement>) => ref.current?.value ?? '';

    const handleSaveAll = () => {
        // console.log('save all click')
        let newConfig = {
            shipment_number: valueOf(shipmentNumberInput),
            shipper_name: valueOf(shipperInput),
            carrier_name: valueOf(carrierInput),
            consignee_name: valueOf(consigneeInput),
            start_location: valueOf(startLocationInput),
            end_location: valueOf(endLocationInput),
            note: valueOf(noteInput),
            notify_emails: [valueOf(emailInput1), valueOf(emailInput2), valueOf(emailInput3)].filter(
                (email: any) => email !== ''
            ),
            alert_time: alertRanges
                .filter((r: any) => r.start && r.end)
                .map((r: any) => ({
                    start_time: dayjs(r.start).subtract(timeOffset, 'm').format('YYYY-MM-DD HH:mm:ss'),
                    end_time: dayjs(r.end).subtract(timeOffset, 'm').format('YYYY-MM-DD HH:mm:ss'),
                })),
            upper_limit: convertToCelsius(parseFloat(valueOf(upLimitInput)), upperUnit),
            lower_limit: convertToCelsius(parseFloat(valueOf(lowLimitInput)), lowerUnit),
            time_limit: convertToSecond(parseInt(valueOf(timeLimitInput)), timeUnit),
            interval: convertToSecond(parseInt(valueOf(intervalInput)), intervalUnit),
            type: rangeType,
        };

        // console.log(newConfig);
        updateRecords.mutate(
            { deviceIds: [deviceId], indexes: [index], record: newConfig },
            { onSuccess: () => setSaveAlert('success'), onError: () => setSaveAlert('error') }
        );

        // const infoForm = document.getElementById('infoForm');
        // const formData = new FormData(infoForm);
        // const data = {};
        // for (let [key, value] of formData.entries()) {
        //     data[key] = value;
        // }
        // console.log(data);
    };

    return (
        <div className='modal__container'>
            <Snackbar open={saveAlert !== null} autoHideDuration={6000} onClose={handleCloseSaveAlert}>
                <Alert onClose={handleCloseSaveAlert} severity={saveAlert ?? 'success'}>
                    {saveAlert === 'error' ? tNotify('saveFailed') : 'Save successfully!'}
                </Alert>
            </Snackbar>
            <form className='modal__form__display' id='infoForm'>
                <div className='modal__block'>
                    <div className='title'>{t('information')}</div>
                    <div className='modal__form__item'>
                        <label className='modal__form__item__label'>{t('shipmentNumber')}</label>
                        <div className='vline'></div>
                        <input
                            name='shipmentNumber'
                            className='modal__form__item__input'
                            type='text'
                            placeholder='1234567890'
                            defaultValue={metaData.shipment_number}
                            ref={shipmentNumberInput}
                        />
                    </div>
                    <hr className='hline'></hr>
                    <div className='modal__form__item'>
                        <label className='modal__form__item__label'>{t('shipper')}</label>
                        <div className='vline'></div>
                        <input
                            name='shipperName'
                            className='modal__form__item__input'
                            type='text'
                            placeholder='XXXXXX'
                            defaultValue={metaData.shipper_name}
                            ref={shipperInput}
                        />
                    </div>
                    <hr className='hline'></hr>
                    <div className='modal__form__item'>
                        <label className='modal__form__item__label'>{t('carrier')}</label>
                        <div className='vline'></div>
                        <input
                            name='carrier'
                            className='modal__form__item__input'
                            type='text'
                            placeholder='XXXXXX'
                            defaultValue={metaData.carrier_name}
                            ref={carrierInput}
                        />
                    </div>
                    <hr className='hline'></hr>
                    <div className='modal__form__item'>
                        <label className='modal__form__item__label'>{t('consignee')}</label>
                        <div className='vline'></div>
                        <input
                            name='consignee'
                            className='modal__form__item__input'
                            type='text'
                            placeholder='XXXXXX'
                            defaultValue={metaData.consignee_name}
                            ref={consigneeInput}
                        />
                    </div>
                    <hr className='hline'></hr>
                    <div className='modal__form__item'>
                        <label className='modal__form__item__label'>{t('startLocation')}</label>
                        <div className='vline'></div>
                        <input
                            name='startLocation'
                            className='modal__form__item__input'
                            type='text'
                            placeholder='XXXXXX'
                            defaultValue={metaData.start_location}
                            ref={startLocationInput}
                        />
                    </div>
                    <hr className='hline'></hr>
                    <div className='modal__form__item'>
                        <label className='modal__form__item__label'>{t('endLocation')}</label>
                        <div className='vline'></div>
                        <input
                            name='endLocation'
                            className='modal__form__item__input'
                            type='text'
                            placeholder='XXXXXX'
                            defaultValue={metaData.end_location}
                            ref={endLocationInput}
                        />
                    </div>
                    <hr className='hline'></hr>
                    <div className='modal__form__item'>
                        <label className='modal__form__item__label'>{t('note')}</label>
                        <div className='vline'></div>
                        <input
                            name='note'
                            className='modal__form__item__input note'
                            type='text'
                            placeholder='XXXXXX'
                            defaultValue={metaData.note}
                            ref={noteInput}
                        />
                    </div>
                </div>
                <div className='modal__block'>
                    <div className='title'>{t('configure')}</div>
                    <div className='modal__form__item__title'>{t('failAlarm')}</div>

                    <hr className='hline'></hr>
                    <div className='modal__form__item'>
                        <label className='modal__form__item__label'>{t('excursionType')}</label>
                        <div className='vline'></div>
                        <div className='rangebar__container__item excursion' style={{ width: '30%' }}>
                            <button
                                disabled={ro}
                                id='continuous'
                                type='button'
                                className={rangeType === 'continuous' ? 'active' : ''}
                                onClick={() => setRangeType('continuous')}
                            >
                                {t('singleEvent')}
                            </button>
                        </div>
                        <div className='rangebar__container__item excursion' style={{ width: '30%' }}>
                            <button
                                disabled={ro}
                                id='cumulative'
                                type='button'
                                className={rangeType === 'cumulative' ? 'active' : ''}
                                onClick={() => setRangeType('cumulative')}
                            >
                                {t('cumulative')}
                            </button>
                        </div>
                    </div>
                    <hr className='hline'></hr>
                    <div className='modal__form__item'>
                        <label className='modal__form__item__label'>{t('upperLimit')}</label>
                        <div className='vline'></div>
                        <input
                            disabled={ro}
                            name='upLimit'
                            className='modal__form__item__input alarm__config'
                            type='text'
                            placeholder={convertTemperature(8, upperUnit)}
                            defaultValue={threshold.upper_limit}
                            ref={upLimitInput}
                        />
                        <button
                            disabled={ro}
                            type='button'
                            className={upperUnit === 'celsius' ? 'active config__button' : 'config__button'}
                            onClick={() => setUpperUnit('celsius')}
                        >
                            °C
                        </button>
                        {/* <button disabled={true} type='button' className='config__button hidden'></button> */}
                        <button
                            disabled={true}
                            type='button'
                            aria-hidden='true'
                            tabIndex={-1}
                            className='config__button hidden'
                        ></button>
                        <button
                            disabled={ro}
                            type='button'
                            className={upperUnit === 'fahrenheit' ? 'active config__button' : 'config__button'}
                            onClick={() => setUpperUnit('fahrenheit')}
                        >
                            °F
                        </button>
                    </div>
                    <hr className='hline'></hr>
                    <div className='modal__form__item'>
                        <label className='modal__form__item__label'>{t('lowerLimit')}</label>
                        <div className='vline'></div>
                        <input
                            disabled={ro}
                            name='lowLimit'
                            className='modal__form__item__input alarm__config'
                            type='text'
                            placeholder={convertTemperature(2, lowerUnit)}
                            defaultValue={threshold.lower_limit}
                            ref={lowLimitInput}
                        />
                        <button
                            disabled={ro}
                            type='button'
                            className={lowerUnit === 'celsius' ? 'active config__button' : 'config__button'}
                            onClick={() => setLowerUnit('celsius')}
                        >
                            °C
                        </button>
                        {/* <button disabled={true} type='button' className='config__button hidden'></button> */}
                        <button
                            disabled={true}
                            type='button'
                            aria-hidden='true'
                            tabIndex={-1}
                            className='config__button hidden'
                        ></button>
                        <button
                            disabled={ro}
                            type='button'
                            className={lowerUnit === 'fahrenheit' ? 'active config__button' : 'config__button'}
                            onClick={() => setLowerUnit('fahrenheit')}
                        >
                            °F
                        </button>
                    </div>
                    <hr className='hline'></hr>
                    <div className='modal__form__item'>
                        <label className='modal__form__item__label'>{t('duration')}</label>
                        <div className='vline'></div>
                        <input
                            disabled={ro}
                            name='timeLimit'
                            className='modal__form__item__input alarm__config'
                            type='text'
                            placeholder='10'
                            defaultValue={thresholdConvert.value}
                            ref={timeLimitInput}
                        />
                        {/* <button disabled={ro} type='button' className={timeUnit === 'second' ? 'active config__button' : 'config__button'} onClick={() => setTimeUnit('second')}>
                            {t('S')}
                        </button> */}
                        <button
                            disabled={ro}
                            type='button'
                            className={timeUnit === 'minute' ? 'active config__button' : 'config__button'}
                            onClick={() => setTimeUnit('minute')}
                        >
                            {t('M')}
                        </button>
                        <button
                            disabled={ro}
                            type='button'
                            className={timeUnit === 'hour' ? 'active config__button' : 'config__button'}
                            onClick={() => setTimeUnit('hour')}
                        >
                            {t('H')}
                        </button>
                        <button
                            disabled={ro}
                            type='button'
                            className={timeUnit === 'day' ? 'active config__button' : 'config__button'}
                            onClick={() => setTimeUnit('day')}
                        >
                            {t('D')}
                        </button>
                    </div>
                    <hr className='hline'></hr>
                    <div className='modal__form__item'>
                        <label className='modal__form__item__label'>{t('workingTime')}</label>
                        {/* Wrap the SVG in a span — MUI Tooltip needs a DOM element that
                            forwards ref/mouse events, which svgr-generated components don't. */}
                        <LightTooltip title={t('workingTimeDiscription')} placement='top'>
                            <span className='cursor-help' style={{ display: 'inline-flex' }}>
                                <Help
                                    style={{
                                        verticalAlign: 'middle',
                                        width: '1rem',
                                    }}
                                />
                            </span>
                        </LightTooltip>
                        <div className='vline'></div>
                        <WorkingTimeRangesEditor
                            resetKey={`${deviceId}-${index}`}
                            initialRanges={alertRanges}
                            offsetString={offsetString}
                            disabled={ro}
                            onRangesChange={handleWorkingTimeChange}
                        />
                    </div>
                    <div className='modal__form__item__title'>{t('dataLogging')}</div>
                    <hr className='hline'></hr>
                    <div className='modal__form__item'>
                        <label className='modal__form__item__label'>{t('loggingInterval')}</label>
                        <div className='vline'></div>
                        <input
                            disabled={true}
                            name='interval'
                            className='modal__form__item__input alarm__config'
                            type='text'
                            placeholder='10'
                            defaultValue={intervalConvert.value}
                            ref={intervalInput}
                        />
                        {/* <button disabled={true} type='button' className={intervalUnit === 'second' ? 'active config__button' : 'config__button'} onClick={() => setIntervalUnit('second')}>
                            {t('S')}
                        </button> */}
                        <button
                            disabled={true}
                            type='button'
                            className={intervalUnit === 'minute' ? 'active config__button' : 'config__button'}
                            onClick={() => setIntervalUnit('minute')}
                        >
                            {t('M')}
                        </button>
                        <button
                            disabled={true}
                            type='button'
                            className={intervalUnit === 'hour' ? 'active config__button' : 'config__button'}
                            onClick={() => setIntervalUnit('hour')}
                        >
                            {t('H')}
                        </button>
                        <button
                            disabled={true}
                            type='button'
                            className={intervalUnit === 'day' ? 'active config__button' : 'config__button'}
                            onClick={() => setIntervalUnit('day')}
                        >
                            {t('D')}
                        </button>
                    </div>
                    <div className='modal__form__item__title'>{t('alarmNotification')}</div>
                    <hr className='hline'></hr>
                    <div className='modal__form__item'>
                        <label className='modal__form__item__label'>{t('email')}</label>
                        <div className='vline'></div>
                        <input
                            disabled={ro}
                            name='email'
                            className='modal__form__item__input'
                            type='text'
                            placeholder='XXXXXX'
                            defaultValue={notifyEmails[0]}
                            ref={emailInput1}
                        />
                    </div>
                    <hr className='hline'></hr>
                    <div className='modal__form__item'>
                        <label className='modal__form__item__label'>{t('email')}</label>
                        <div className='vline'></div>
                        <input
                            disabled={ro}
                            key='email'
                            name='email'
                            className='modal__form__item__input'
                            type='text'
                            placeholder='XXXXXX'
                            defaultValue={notifyEmails[1]}
                            ref={emailInput2}
                        />
                    </div>
                    <hr className='hline'></hr>
                    <div className='modal__form__item'>
                        <label className='modal__form__item__label'>{t('email')}</label>
                        <div className='vline'></div>
                        <input
                            disabled={ro}
                            key='email'
                            name='email'
                            className='modal__form__item__input'
                            type='text'
                            placeholder='XXXXXX'
                            defaultValue={notifyEmails[2]}
                            ref={emailInput3}
                        />
                    </div>
                </div>
            </form>
            {!ro && (
                <div className='button__container'>
                    <button className='save__all' onClick={handleSaveAll}>
                        {t('saveAll')}
                    </button>
                </div>
            )}
        </div>
    );
}

export default Config;
